import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Main from '../../../../BrowserSupport/Main';
import I18nTitle from '../../../../Title/I18nTitle';
import MainNav from '../../../Platform/MainNav';
import { createNewGroup, createSelectedGroup } from '../../../Form/GroupInputWithSuggestions/Types';
import { trackIntercomEvent } from '../../../../services/intercomTrackingService';
import ParticipantAdded from './ParticipantAdded';
import ErrorBoundary from './ErrorBoundary';
import ParticipantDetailsSection from './ParticipantDetailsSection';
import GroupSelectionSection from './GroupSelectionSection';
import LicenseSelectionSection from './LicenseSelectionSection';
import CancelConfirmationModal from './CancelConfirmationModal';
import { participantOptionsStore } from './Stores/ParticipantOptionsStore';
import { participantStore } from './Stores/ParticipantStore';
import { formStore } from './Stores/FormStore';
import { createSelectedLicense } from './Stores/Types';

function AddParticipant() {
  const navigate = useNavigate();

  useEffect(() => {
    participantStore.reset();
    participantOptionsStore.reload();
    formStore.reset();
    trackIntercomEvent('Add Participant - Page Loaded');
  }, []);

  const isSubmitting = participantStore.state.kind === 'creating';
  const canSubmit = participantStore.canSubmit;

  if (participantOptionsStore.state.kind !== 'ready') return null;

  const allGroups = participantOptionsStore.state.participantOptionsResource.payload.userGroups;
  const allLicenses = participantOptionsStore.state.participantOptionsResource.payload.licenses;

  const handleConfirm = () => {
    trackIntercomEvent('Add Participant - Submitted', {
      groupCount: formStore.selectedGroupIds.length,
      licenseCount: formStore.selectedLicenseIds.length,
      isAuditor: formStore.areLicensesAuditor,
    });

    // Map selected group IDs to SelectableUserGroup[]
    const selectedGroups = formStore.selectedGroupIds
      .map((id: number) => {
        // Check if it's a new group (negative ID)
        if (id < 0) {
          const groupName = formStore.newGroupIdMap.get(id);
          return groupName ? createNewGroup(groupName) : null;
        }
        // Otherwise, it's an existing group
        const group = allGroups.find((g) => g.id === id);
        if (!group) return null;

        // Convert UserGroupWithAssociations to UserGroup (base type with optional fields)
        const userGroup = {
          id: group.id,
          name: group.name,
          organization: group.organization,
          memberCount: group.memberCount,
          assignedLicenseCount: group.assignedLicenseCount,
          assignedLicenseIds: group.assignedLicenseIds,
        };

        return createSelectedGroup(userGroup);
      })
      .filter((g): g is NonNullable<typeof g> => g !== null);

    // Map selected license IDs to SelectableLicense[]
    const selectedLicenses = formStore.selectedLicenseIds
      .map((id: number) => {
        const license = allLicenses.find((l) => l.id === id);
        return license ? createSelectedLicense(license) : null;
      })
      .filter((l): l is NonNullable<typeof l> => l !== null);

    // Update the store with selections
    participantStore.updateUserGroups(selectedGroups);
    participantStore.updateLicenses(selectedLicenses);
    participantStore.updateAreLicensesAuditor(formStore.areLicensesAuditor);

    // Submit to the API
    participantStore.create();
  };

  const handleGroupToggle = (groupId: number, checked: boolean) => {
    const group = allGroups.find((g) => g.id === groupId);

    const newSelectedGroupIds = checked
      ? [...formStore.selectedGroupIds, groupId]
      : formStore.selectedGroupIds.filter((id: number) => id !== groupId);

    const allSuggestedLicenseIds = new Set<number>();
    newSelectedGroupIds.forEach((gId: number) => {
      const g = allGroups.find((gr) => gr.id === gId);
      if (g && g.associatedLicenseIds.length > 0) {
        g.associatedLicenseIds.forEach((licId) => allSuggestedLicenseIds.add(licId));
      }
    });

    if (checked && group) {
      const associatedIds = group.associatedLicenseIds;
      formStore.updateSelectedGroupIds(newSelectedGroupIds);
      formStore.updateGroupSuggestedLicenseIds(allSuggestedLicenseIds);
      if (associatedIds.length > 0) {
        formStore.updateSelectedLicenseIds([
          ...new Set([...formStore.selectedLicenseIds, ...associatedIds]),
        ]);
      }
    } else {
      const oldSuggestedLicenseIds = formStore.groupSuggestedLicenseIds;
      const newSelectedLicenseIds = formStore.selectedLicenseIds.filter((id) => {
        if (allSuggestedLicenseIds.has(id)) return true;
        if (!oldSuggestedLicenseIds.has(id)) return true;
        return false;
      });
      formStore.updateSelectedGroupIds(newSelectedGroupIds);
      formStore.updateGroupSuggestedLicenseIds(allSuggestedLicenseIds);
      formStore.updateSelectedLicenseIds(newSelectedLicenseIds);

      if (newSelectedLicenseIds.length === 0 && formStore.areLicensesAuditor) {
        formStore.updateAreLicensesAuditor(false);
      }
    }
  };

  const handleLicenseToggle = (licenseId: number, checked: boolean) => {
    if (formStore.groupSuggestedLicenseIds.has(licenseId)) return;

    const newSelectedLicenseIds = checked
      ? [...formStore.selectedLicenseIds, licenseId]
      : formStore.selectedLicenseIds.filter((id) => id !== licenseId);

    formStore.updateSelectedLicenseIds(newSelectedLicenseIds);

    if (newSelectedLicenseIds.length === 0 && formStore.areLicensesAuditor) {
      formStore.updateAreLicensesAuditor(false);
    }
  };

  const toggleLicenseTypeExpanded = (typeName: string) => {
    const next = new Set(formStore.expandedLicenseTypes);
    if (next.has(typeName)) {
      next.delete(typeName);
    } else {
      next.add(typeName);
    }
    formStore.updateExpandedLicenseTypes(next);
  };

  const handleCreateNewGroup = () => {
    if (!formStore.newGroupName.trim()) return;

    trackIntercomEvent('Add Participant - Created New Group', {
      groupName: formStore.newGroupName.trim(),
    });

    // Add the new group to the store
    const groupName = formStore.newGroupName.trim();
    const newGroup = createNewGroup(groupName);
    participantStore.addUserGroup(newGroup);

    // Generate a stable negative ID using timestamp to avoid collisions
    const newGroupId = -Date.now();

    // Update state: add group mapping, select it, and clear form
    const newMap = new Map(formStore.newGroupIdMap);
    newMap.set(newGroupId, groupName);
    formStore.updateNewGroupIdMap(newMap);
    formStore.updateSelectedGroupIds([...formStore.selectedGroupIds, newGroupId]);
    formStore.updateNewGroupName('');
    formStore.updateShowCreateGroupInput(false);
    formStore.updateGroupSearchQuery('');
  };

  const handleCreateFromSearch = (groupName: string) => {
    trackIntercomEvent('Add Participant - Created New Group', { groupName });
    const newGroup = createNewGroup(groupName);
    participantStore.addUserGroup(newGroup);
  };

  const handleCancelClick = () => {
    formStore.updateShowCancelConfirmation(true);
  };

  const handleDiscardChanges = () => {
    trackIntercomEvent('Add Participant - Cancelled');
    formStore.updateShowCancelConfirmation(false);
    navigate('/people/participants');
  };

  const handleKeepEditing = () => {
    formStore.updateShowCancelConfirmation(false);
  };

  // Helper to get license details for tooltip
  return (
    <>
      <I18nTitle title="People" />
      <div className="min-h-screen flex flex-col">
        <MainNav />
        <div className="border-b border-border bg-background py-6">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl" style={{ fontWeight: 'var(--font-weight-semibold)' }}>
                  Add Participant
                </h1>
                <p className="text-muted-foreground">
                  Add a participant and optionally assign groups and allocate licenses.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  className="px-4 py-2 text-primary hover:opacity-80 transition-opacity"
                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                  onClick={handleCancelClick}
                  data-intercom-target="add-participant-cancel-button"
                >
                  Cancel
                </button>
                <button
                  className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontWeight: 'var(--font-weight-medium)' }}
                  disabled={!canSubmit}
                  onClick={handleConfirm}
                  data-intercom-target="add-participant-submit-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Adding...
                    </span>
                  ) : (
                    'Add Participant'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="flex-grow bg-muted/30">
          <div className="max-w-5xl mx-auto px-12 py-8">
            <Main id="main">
              <div className="space-y-6">
                {/* Section 1: Participant Details */}
                <ParticipantDetailsSection />

                {/* Section 2: Groups */}
                <GroupSelectionSection
                  formStore={formStore}
                  groups={allGroups}
                  licenses={allLicenses}
                  handleGroupToggle={handleGroupToggle}
                  handleCreateNewGroup={handleCreateNewGroup}
                  onCreateFromSearch={handleCreateFromSearch}
                />

                {/* Section 3: Assign Licenses */}
                <LicenseSelectionSection
                  formStore={formStore}
                  groups={allGroups}
                  licenses={allLicenses}
                  canAddAsAuditor={
                    participantOptionsStore.state.participantOptionsResource.payload.canAddAsAuditor
                  }
                  handleLicenseToggle={handleLicenseToggle}
                  toggleLicenseTypeExpanded={toggleLicenseTypeExpanded}
                />
              </div>
            </Main>
          </div>
        </div>
        <div className="border-t border-border bg-card py-4">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-center justify-between">
              <button
                className="text-primary hover:opacity-80 transition-opacity"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
                onClick={handleCancelClick}
                data-intercom-target="add-participant-cancel-button-bottom"
              >
                Cancel
              </button>
              <button
                className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ fontWeight: 'var(--font-weight-medium)' }}
                disabled={!canSubmit}
                onClick={handleConfirm}
                data-intercom-target="add-participant-submit-button-bottom"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Adding...
                  </span>
                ) : (
                  'Add Participant'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Cancel Confirmation Modal */}
        <CancelConfirmationModal
          isOpen={formStore.showCancelConfirmation}
          onKeepEditing={handleKeepEditing}
          onDiscardChanges={handleDiscardChanges}
        />

        <ParticipantAdded />
      </div>
    </>
  );
}

const ObservedAddParticipant = observer(AddParticipant);

export default function AddParticipantWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <ObservedAddParticipant />
    </ErrorBoundary>
  );
}
